import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";

import { useQuery, useSubscription } from "@apollo/client";
import {
  ChromeReaderMode as ChromeReaderModeIcon,
  Dashboard as DashboardIcon,
  DateRange as DateRangeIcon,
  Feedback as FeedbackIcon,
  PieChart as PieChartIcon,
  RateReview as RateReviewIcon,
  Sms as SmsIcon,
} from "@mui/icons-material";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import { Badge, Box, Drawer, List, Typography } from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import { Features } from "@/config/features";
import { graphql } from "@/gql";
import useBooleanToggle from "@/hooks/useBooleanToggle";
import SupportIcon from "@components/Icons/SupportIcon";

// import ClickComponents from "./ClickComponents";
import ClientList from "./ClickComponents/Client/ClientList";
import FeedbackModal from "./components/FeedbackModal";
import ListItemButton from "./ListItemButton";
import ListItemClient from "./ListItemClient";
import ListItemHref from "./ListItemHref";
import ListItemLink from "./ListItemLink";
import ListItemSublist from "./ListItemSublist";
import { APP_BAR_HEIGHT } from "../AppBar";
import DatabaseIcon from "../Icons/DatabaseIcon";
import FormPaperIcon from "../Icons/FormPaperIcon";
import NewIcon from "../Icons/NewIcon";

type State = {
  openedComponent: string;
  keepOpen: boolean;
};

type Action =
  | { type: "open-component"; payload: string /** name of the component **/ }
  | { type: "close-component" }
  | { type: "keep-open"; payload: boolean };

type Value = { state: State; dispatch: React.Dispatch<Action> };

const SideBarContext = createContext<Value | null>(null);

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case "open-component":
      return {
        ...state,
        openedComponent: action.payload,
      };
    case "keep-open":
      return {
        ...state,
        keepOpen: action.payload,
      };
    case "close-component":
      return initialState;
    default:
      return state;
  }
};

const initialState: State = {
  openedComponent: "",
  keepOpen: false,
};

const drawerWidthCollapsed = 60;
const drawerWidth = 240;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
  },

  customLink: {
    color: theme.palette.primary.main,
    "& .MuiListItemIcon-root": {
      color: "inherit",
    },
    "& .MuiListItemText-root": {
      color: "inherit",
    },
    textDecoration: "none",
  },

  customLinkContainer: {
    display: "flex",
    flexDirection: "row",
    alignContent: "center",
    margin: "18px 18px",
  },

  drawerContainer: {
    position: "fixed",
    zIndex: theme.zIndex.drawer,
  },

  drawer: {
    position: "relative",
    flexShrink: 0,
    minWidth: drawerWidthCollapsed,
    width: drawerWidth,
    whiteSpace: "nowrap",
    overflowX: "hidden",
  },

  drawerSpace: {
    minWidth: drawerWidthCollapsed,
    width: drawerWidth,
  },

  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  drawerClose: {
    width: drawerWidthCollapsed,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  drawerShadow: {
    // taken from Card component box-shadow style
    boxShadow: "0px 10px 30px rgb(11 27 38 / 5%)",
  },

  paper: {
    height: `calc(100% - ${APP_BAR_HEIGHT}px - 2px)`, // -2px for that weird gap between appbar and sidebar
    marginTop: APP_BAR_HEIGHT + 2, // +2px for that weird gap between appbar and sidebar
    overflowX: "hidden",
  },

  sidePanel: {
    flexGrow: 1,
    position: "relative",
    marginLeft: drawerWidthCollapsed,
    transition: theme.transitions.create("margin-left", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  sidePanelExpanded: {
    marginLeft: drawerWidth,
    transition: theme.transitions.create("margin-left", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  sideBarText: {
    fontWeight: 600,
    marginLeft: theme.spacing(6),
  },

  textLink: {
    color: "inherit",
    textDecoration: "none",
  },

  unreadMessageBadge: {
    marginRight: 14,
  },
}));

const StyledFeedBackIcon = withStyles(FeedbackIcon, {
  root: {
    color: "#7d8798",
  },
});

const StyledRateReviewIcon = withStyles(RateReviewIcon, {
  root: {
    color: "#7d8798",
  },
});
interface Props {
  collapsed?: boolean;
}

const sidebarQuery = graphql(`
  query Sidebar {
    coachViewer {
      id
      unreadMessagesCount
      npsFeedbackRequested
    }
  }
`);

export const unreadMessagesSubscription = graphql(`
  subscription UnreadMessages {
    synchronize(model: "Message") {
      node {
        ... on Message {
          id
        }
      }
    }
  }
`);

const Sidebar: React.FC<Props> = ({ collapsed = false }) => {
  const { classes, cx } = useStyles();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [hovered, setHovered] = useBooleanToggle(false);
  const [modalVisibility, setModalVisibility] = useState(false);
  const { data, refetch } = useQuery(sidebarQuery, {
    fetchPolicy: "network-only",
  });
  useSubscription(unreadMessagesSubscription, {
    onData: ({ data }) => {
      if (!data) return;
      refetch();
    },
  });
  const drawerShadowShown =
    collapsed && hovered && state.openedComponent === "";
  const drawerOpened = !collapsed || hovered;
  const drawerClosed = collapsed && !hovered;

  useEffect(() => {
    if (data?.coachViewer.npsFeedbackRequested) setModalVisibility(true);
  }, [data?.coachViewer.npsFeedbackRequested]);

  return (
    <div className={classes.root}>
      <SideBarContext.Provider value={{ state, dispatch }}>
        <div
          className={classes.drawerContainer}
          onMouseEnter={() => collapsed && setHovered.setTrue()}
          onMouseLeave={() => collapsed && setHovered.setFalse()}
        >
          <Drawer
            variant="permanent"
            className={cx(classes.drawer, {
              [classes.drawerOpen]: drawerOpened,
              [classes.drawerClose]: drawerClosed,
            })}
            classes={{
              paper: cx(classes.paper, {
                [classes.drawerOpen]: drawerOpened,
                [classes.drawerClose]: drawerClosed,
                [classes.drawerShadow]: drawerShadowShown,
              }),
            }}
          >
            <List>
              <ListItemLink
                to="/"
                exact
                text="Dashboard"
                icon={<DashboardIcon />}
              />
              <ListItemLink
                to="/messages"
                text="Messages"
                icon={<SmsIcon />}
                trailingIcon={
                  <Badge
                    className={classes.unreadMessageBadge}
                    badgeContent={data?.coachViewer.unreadMessagesCount}
                    color="primary"
                  />
                }
              />
              <ListItemClient
                onClick={() =>
                  dispatch({
                    type: "open-component",
                    payload: "clients",
                  })
                }
              />
              {Features.appointmentsEnabled && (
                <ListItemLink
                  to="/appointments"
                  text="Appointments"
                  icon={<DateRangeIcon />}
                />
              )}
              <ListItemLink
                to="/scheduler"
                text="Scheduler"
                icon={<EditCalendarIcon />}
              />
              {Features.businessReportsEnabled && (
                <ListItemSublist text="Business Report" icon={<PieChartIcon />}>
                  <List>
                    <ListItemLink
                      to="/reports/compliance"
                      text="Compliance"
                      subItem
                    />
                    <ListItemLink
                      to="/reports/progress"
                      text="Progress"
                      subItem
                    />
                    <ListItemLink
                      to="/reports/payments"
                      text="Payments"
                      subItem
                    />
                    <ListItemLink
                      to="/reports/appointment"
                      text="Appointment"
                      subItem
                    />
                  </List>
                </ListItemSublist>
              )}
              <ListItemSublist text="Templates" icon={<ChromeReaderModeIcon />}>
                <List>
                  {Features.programTemplatesContentEnabled && (
                    <ListItemLink
                      to="/templates/programs"
                      text="Programs"
                      subItem
                    />
                  )}
                  <ListItemLink to="/templates/meals" text="Meals" subItem />
                  {Features.exerciseTemplatesContentEnabled && (
                    <ListItemLink
                      to="/templates/exercise"
                      text="Exercise"
                      subItem
                    />
                  )}
                </List>
              </ListItemSublist>
              <ListItemSublist text="Database" icon={<DatabaseIcon />}>
                <List>
                  <ListItemLink to="/database/recipes" text="Recipes" subItem />
                </List>
              </ListItemSublist>
              {Features.formsEnabled && (
                <ListItemLink
                  to="/forms"
                  text="Forms"
                  icon={<FormPaperIcon />}
                />
              )}
              {Features.whatsNewContent && (
                <ListItemLink
                  to="/whats-new"
                  text="What's New"
                  icon={<NewIcon />}
                />
              )}
              {Features.reportIssueEnabled && (
                <a
                  className={classes.textLink}
                  href="https://nutricoach.atlassian.net/servicedesk/customer/portal/2"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Box className={classes.customLinkContainer}>
                    <StyledFeedBackIcon />
                    <Typography
                      className={classes.sideBarText}
                      color="textPrimary"
                    >
                      Report Issues
                    </Typography>
                  </Box>
                </a>
              )}
              <ListItemButton
                text="Rate us!"
                icon={<StyledRateReviewIcon />}
                onClick={() => setModalVisibility(true)}
              />
              <ListItemHref
                href="https://support.nutricoach.com"
                rel="noopener noreferrer"
                target="_blank"
                text="Support"
                icon={<SupportIcon />}
              />
            </List>
          </Drawer>
          <div
            className={cx(classes.sidePanel, {
              [classes.sidePanelExpanded]: drawerOpened,
            })}
          >
            <ClientList />
          </div>
        </div>
        <div
          className={cx(classes.drawerSpace, {
            [classes.drawerOpen]: !collapsed,
            [classes.drawerClose]: collapsed,
          })}
        />
      </SideBarContext.Provider>
      <FeedbackModal
        visible={modalVisibility}
        onClose={() => setModalVisibility(false)}
      />
    </div>
  );
};

export const useSidebarContext = (): Value => {
  const context = useContext(SideBarContext);

  if (context == null) throw new Error("No SidebarContext provider found.");

  return context;
};

export default Sidebar;
